import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const options = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
];

/**
 * Minimalist language switcher — globe icon in gold + refined dropdown.
 * `variant="dark"` styles the trigger for placement on a dark background.
 */
export const LanguageSwitcher = ({ variant = "light", direction = "down" }) => {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const current = options.find((o) => o.code === lang) || options[0];
  const triggerBase =
    variant === "dark"
      ? "text-white/70 hover:text-gold_soft"
      : "text-ink hover:text-gold";
  const chevronColor =
    variant === "dark" ? "text-white/50" : "text-muted_text";

  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.label")}
        data-testid="lang-switcher-trigger"
        className={`group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] transition-colors py-2 ${triggerBase}`}
      >
        <Globe className="w-4 h-4 text-gold" strokeWidth={1.4} />
        <span>{current.label}</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-300 ${chevronColor} ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={1.4}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute right-0 min-w-[180px] bg-white border border-ink/10 shadow-soft-lift py-2 z-[70] ${
            direction === "up" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {options.map((opt) => {
            const active = opt.code === lang;
            return (
              <button
                key={opt.code}
                role="option"
                aria-selected={active}
                data-testid={`lang-option-${opt.code}`}
                onClick={() => {
                  setLang(opt.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-soft ${
                  active
                    ? "text-ink hover:text-gold"
                    : "text-muted_text hover:text-gold"
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors ${
                    active ? "border-gold" : "border-ink/25"
                  }`}
                >
                  {active && <Check className="w-2.5 h-2.5 text-gold" strokeWidth={2.5} />}
                </span>
                <span className="font-display">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
